import { useEffect, useState, useRef } from "react";
import { Loader, Text, Stack, Center, Button, Group } from "@mantine/core";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AppRoutes, backend_url } from "@/constants";

const PaymentLoader = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [showErrorOptions, setShowErrorOptions] = useState(false);
  const pollCountRef = useRef(0);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      try {
        const sessionId = searchParams.get("session_id");
        const response = await axios.post(
          `${backend_url}/api/stripe/management/verify-payment/`,
          {
            session_id: sessionId,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.content?.isActive) {
          console.log("Subscription is active, navigating to onboarding");
          try {
            const liteFlag = localStorage.getItem(
              "onboarding_client_creation_flow"
            );
            const goLite = liteFlag === "true" || liteFlag === "1";

            // Clean up flags
            localStorage.removeItem("onboarding_new_flow");
            localStorage.removeItem("onboarding_payment_in_progress");

            // Navigate to appropriate onboarding route
            if (goLite) {
              navigate(AppRoutes.liteOnboarding);
            } else {
              // Always use new onboarding flow
              navigate(`${AppRoutes.gettingStarted}?payment_success=true`);
            }
          } catch {
            // Fallback to new onboarding
            navigate(`${AppRoutes.gettingStarted}?payment_success=true`);
          }
        } else {
          console.log("Subscription is not active, polling again");
          pollCountRef.current += 1;
          if (pollCountRef.current >= 3) {
            // After 3 attempts
            setShowErrorOptions(true);
            return true; // Signal to stop polling
          }
        }
      } catch (err) {
        setError("Failed to check subscription status. Please try again.");
        pollCountRef.current += 1;
        if (pollCountRef.current >= 3) {
          setShowErrorOptions(true);
          return true; // Signal to stop polling
        }
      }
      return false; // Continue polling
    };

    // Initial check
    checkSubscriptionStatus();

    // Set up polling every 3 seconds
    const intervalId = setInterval(async () => {
      const shouldStop = await checkSubscriptionStatus();
      if (shouldStop) {
        clearInterval(intervalId);
      }
    }, 3000);

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [navigate, searchParams]);

  const handleSupportClick = () => {
    window.location.href = "mailto:help@smartwiz.io";
  };

  const handleLoginClick = () => {
    navigate(AppRoutes.login);
  };

  if (showErrorOptions) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text size="lg" fw={500} c="red">
            There was an error processing your payment
          </Text>
          <Text size="md" c="dimmed">
            Please contact support or try logging in again
          </Text>
          <Group>
            <Button onClick={handleSupportClick} variant="outline">
              Contact Support
            </Button>
            <Button onClick={handleLoginClick}>Go to Login</Button>
          </Group>
        </Stack>
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Stack align="center" gap="md">
        <Loader size="lg" color="blue" />
        <Text size="lg" fw={500}>
          Please do not refresh or go back to prevent issues or multiple charges
        </Text>
        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}
      </Stack>
    </Center>
  );
};

export default PaymentLoader;
